// The campaign's opening cash on hand, a column on the finance settings row. Each write checks
// and bumps that row's token, so the hooks write the returned result and token into the cached
// settings and refetch them after a conflict.

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import {
  isStaleWrite,
  type FinanceSettingsDto,
  type OpeningBalanceClearRequest,
  type OpeningBalanceRequest,
  type OpeningBalanceResult,
} from '@signalvote/shared';
import { financeQueries, queryKeys } from '../../lib/queryKeys';

function settle(queryClient: QueryClient) {
  return {
    onSuccess: (result: OpeningBalanceResult) => {
      queryClient.setQueryData<FinanceSettingsDto | null>(queryKeys.finance.settings(), (settings) =>
        settings ? { ...settings, ...result } : settings,
      );
      queryClient.invalidateQueries(financeQueries);
    },
    onError: (error: unknown) => {
      if (isStaleWrite(error))
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.settings() });
    },
  };
}

export function useRecordOpeningBalance() {
  const queryClient = useQueryClient();
  return useMutation<OpeningBalanceResult, Error, OpeningBalanceRequest>({
    mutationFn: (body) => getApiClient().openingBalance.recordOpeningBalance(body),
    ...settle(queryClient),
  });
}

export function useUpdateOpeningBalance() {
  const queryClient = useQueryClient();
  return useMutation<OpeningBalanceResult, Error, OpeningBalanceRequest>({
    mutationFn: (body) => getApiClient().openingBalance.updateOpeningBalance(body),
    ...settle(queryClient),
  });
}

export function useClearOpeningBalance() {
  const queryClient = useQueryClient();
  return useMutation<OpeningBalanceResult, Error, OpeningBalanceClearRequest>({
    mutationFn: (body) => getApiClient().openingBalance.clearOpeningBalance(body),
    ...settle(queryClient),
  });
}
